import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  EN: {
    // Common
    dashboard: 'Dashboard',
    roster: 'Roster',
    athletes: 'Athletes',
    teams: 'Teams',
    planner: 'Planner',
    wellness: 'Wellness',
    vbt: 'VBT',
    rtp: 'RTP',
    gps: 'GPS / Load',
    reports: 'Reports',
    admin: 'Admin',
    settings: 'Settings',
    logout: 'Logout',
    login: 'Login',
    email: 'Email',
    password: 'Password',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    active: 'Active',
    inactive: 'Inactive',
    status: 'Status',
    actions: 'Actions',
    view: 'View',
    name: 'Name',
    position: 'Position',
    team: 'Team',
    
    // Athletes
    athleteList: 'Athlete List',
    addAthlete: 'Add Athlete',
    editAthlete: 'Edit Athlete',
    firstName: 'First Name',
    lastName: 'Last Name',
    birthDate: 'Birth Date',
    primaryPosition: 'Primary Position',
    secondaryPositions: 'Secondary Positions',
    jerseyNumber: 'Jersey Number',
    notes: 'Notes',
    primaryTeam: 'Primary Team',
    secondaryTeam: 'Secondary Team',
    assignToTeam: 'Assign to Team',
    teamAssignments: 'Team Assignments',
    assignmentHistory: 'Assignment History',
    
    // Status
    fulltrain: 'Full Training',
    rtpStatus: 'Return to Play',
    out: 'Out / Injured',
    
    // Teams
    teamList: 'Team List',
    addTeam: 'Add Team',
    editTeam: 'Edit Team',
    teamCode: 'Team Code',
    ageGroup: 'Age Group',
    category: 'Category',
    season: 'Season',
    description: 'Description',
    staffAssignments: 'Staff Assignments',
    assignStaff: 'Assign Staff',
    
    // Auth
    signIn: 'Sign In',
    signInDescription: 'Enter your credentials to access the platform',
    invalidCredentials: 'Invalid email or password',
    accountLocked: 'Account is temporarily locked',
    
    // Messages
    loading: 'Loading...',
    noResults: 'No results found',
    confirmDelete: 'Are you sure you want to delete this item?',
    saveSuccess: 'Saved successfully',
    deleteSuccess: 'Deleted successfully',
    error: 'An error occurred',
  },
  IT: {
    // Common
    dashboard: 'Dashboard',
    roster: 'Rosa',
    athletes: 'Atleti',
    teams: 'Squadre',
    planner: 'Pianificatore',
    wellness: 'Wellness',
    vbt: 'VBT',
    rtp: 'RTP',
    gps: 'GPS / Carico',
    reports: 'Report',
    admin: 'Admin',
    settings: 'Impostazioni',
    logout: 'Esci',
    login: 'Accedi',
    email: 'Email',
    password: 'Password',
    save: 'Salva',
    cancel: 'Annulla',
    edit: 'Modifica',
    delete: 'Elimina',
    create: 'Crea',
    search: 'Cerca',
    filter: 'Filtra',
    all: 'Tutti',
    active: 'Attivo',
    inactive: 'Inattivo',
    status: 'Stato',
    actions: 'Azioni',
    view: 'Visualizza',
    name: 'Nome',
    position: 'Posizione',
    team: 'Squadra',
    
    // Athletes
    athleteList: 'Lista Atleti',
    addAthlete: 'Aggiungi Atleta',
    editAthlete: 'Modifica Atleta',
    firstName: 'Nome',
    lastName: 'Cognome',
    birthDate: 'Data di Nascita',
    primaryPosition: 'Posizione Principale',
    secondaryPositions: 'Posizioni Secondarie',
    jerseyNumber: 'Numero Maglia',
    notes: 'Note',
    primaryTeam: 'Squadra Principale',
    secondaryTeam: 'Squadra Secondaria',
    assignToTeam: 'Assegna a Squadra',
    teamAssignments: 'Assegnazioni Squadra',
    assignmentHistory: 'Storico Assegnazioni',
    
    // Status
    fulltrain: 'Allenamento Completo',
    rtpStatus: 'Ritorno al Gioco',
    out: 'Fuori / Infortunato',
    
    // Teams
    teamList: 'Lista Squadre',
    addTeam: 'Aggiungi Squadra',
    editTeam: 'Modifica Squadra',
    teamCode: 'Codice Squadra',
    ageGroup: 'Categoria Età',
    category: 'Categoria',
    season: 'Stagione',
    description: 'Descrizione',
    staffAssignments: 'Assegnazioni Staff',
    assignStaff: 'Assegna Staff',
    
    // Auth
    signIn: 'Accedi',
    signInDescription: 'Inserisci le credenziali per accedere alla piattaforma',
    invalidCredentials: 'Email o password non validi',
    accountLocked: 'Account temporaneamente bloccato',
    
    // Messages
    loading: 'Caricamento...',
    noResults: 'Nessun risultato trovato',
    confirmDelete: 'Sei sicuro di voler eliminare questo elemento?',
    saveSuccess: 'Salvato con successo',
    deleteSuccess: 'Eliminato con successo',
    error: 'Si è verificato un errore',
  },
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('fb_language') || 'EN';
  });

  useEffect(() => {
    localStorage.setItem('fb_language', language);
  }, [language]);

  const t = (key, italianFallback) => {
    // Two-argument convention used widely across the codebase:
    //   t('English text', 'Testo italiano')
    // → returns the Italian arg when language === 'IT', otherwise the English arg.
    // Legacy single-arg call falls through to the dictionary, then returns the key
    // verbatim as a last-ditch fallback (handy when the key IS the English text).
    if (italianFallback !== undefined) {
      return language === 'IT' ? italianFallback : key;
    }
    return translations[language]?.[key] || translations['EN']?.[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'EN' ? 'IT' : 'EN'));
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
